import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../auth';
import { useLanguage } from '../i18n';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Copy, Wallet } from 'lucide-react';
import { toast } from 'sonner';

export default function BalancePage() {
  const { t, getProductName } = useLanguage();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (user?.company_id) fetchData();
  }, [user]);

  const fetchData = async () => {
    const res = await axios.get(`${API}/orders?company_id=${user.company_id}`);
    setOrders(res.data.filter(o =>
      o.payment_status === 'unpaid' || o.payment_status === 'partially_paid'
    ));
  };

  const formatCurrency = (val) => `₩${Number(val).toLocaleString('ko-KR')}`;
  const total = orders.reduce((sum, o) => sum + o.total_amount, 0);

  const copyPaymentInfo = (order) => {
    const ref = `${order.company?.client_code}-${order.order_number}`;
    const text = `결제 정보 (Payment Info)
회사 / Company: ${order.company?.company_name}
주문 번호 / Order: ${order.order_number}
금액 / Amount: ${formatCurrency(order.total_amount)}
참조 코드 / Reference: ${ref}

은행 / Bank: KB국민은행
계좌 / Account: 123-456-7890
예금주 / Holder: Craft Food Factory`;
    navigator.clipboard.writeText(text);
    toast.success(t('copied'));
  };

  return (
    <div className="space-y-6" data-testid="balance-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('outstandingBalance')}</h1>
        <p className="text-gray-500 mt-1">Unpaid invoices</p>
      </div>

      <Card className="p-6 bg-gradient-to-br from-[#1A1A1A] to-[#2C2C2C] text-white border-0">
        <p className="text-sm font-medium opacity-90 uppercase tracking-wide">{t('totalAmount')}</p>
        <p className="text-4xl font-bold mt-2 cff-text-numeric">{formatCurrency(total)}</p>
        <p className="text-sm opacity-80 mt-1">{orders.length} {t('orders').toLowerCase()}</p>
      </Card>

      {orders.length === 0 ? (
        <Card className="p-12 text-center">
          <Wallet className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">All paid up!</p>
          <p className="text-sm text-gray-500 mt-1">No outstanding balance</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {orders.map(o => (
            <Card key={o.id} className="p-5" data-testid={`balance-card-${o.order_number}`}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <p className="font-mono text-sm text-gray-500">{o.order_number}</p>
                  <p className="text-sm text-gray-700 cff-text-numeric mt-0.5">
                    {new Date(o.order_date).toLocaleDateString()}
                  </p>
                  <p className="text-xl font-bold cff-text-numeric mt-1">{formatCurrency(o.total_amount)}</p>
                  <p className="text-xs text-gray-500 mt-1">Ref: {o.company?.client_code}-{o.order_number}</p>
                </div>
                <Button onClick={() => copyPaymentInfo(o)} className="cff-button-primary" data-testid={`copy-payment-${o.order_number}`}>
                  <Copy className="h-4 w-4 mr-2" /> {t('copyPaymentInfo')}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
